import type { IGetCommentResponse } from "../../../api/types"
import type { CommentsSortUi } from "../../../api/types/projects"
import { DEFAULT_COMMENTS_SORT } from "./commentsSortQuery"
import { patchCommentNodePreserveChildren } from "../../../api/commentTreePatch"

/** Индекс выдачи GET с parent_id — DESC; связь children на бэкенде — ASC — после merge выравниваем порядок здесь. */
export function sortCommentsByPublishedAtAsc(nodes: IGetCommentResponse[]): IGetCommentResponse[] {
    return [...nodes].sort((a, b) => {
        const parseTime = (iso: string | null): number | null => {
            if (iso == null || iso === "") return null
            const t = Date.parse(iso)
            return Number.isNaN(t) ? null : t
        }
        const ta = parseTime(a.published_at)
        const tb = parseTime(b.published_at)
        if (ta !== null && tb !== null && ta !== tb) return ta - tb
        if (ta !== null && tb === null) return -1
        if (ta === null && tb !== null) return 1
        return a.id - b.id
    })
}

/**
 * Встраивает в дерево корневых комментариев результат GET с parent_id.
 *
 * Политика (текущий бэкенд): индекс — cursor-пагинация; первая страница для parent_id
 * обычно шире, чем eager-limit вложенных children (5), но уже не полный список при total > per_page.
 * Здесь выполняется полная замена массива прямых потомков узла на массив из ответа —
 * после «Показать ещё» массив потомков перед вызовом сортируют по published_at ASC (см. sortCommentsByPublishedAtAsc).
 *
 * TODO: если понадобится подгружать следующие страницы cursor для той же ветки —
 * заменить на merge с дедупом по id и передачей cursor из meta/links.
 */
export function mergeChildrenIntoParent(
    roots: IGetCommentResponse[],
    parentId: number,
    newNodes: IGetCommentResponse[]
): IGetCommentResponse[] {
    return roots.map((node) => {
        if (node.id === parentId) {
            return { ...node, children: [...newNodes] }
        }
        if (node.children?.length) {
            return { ...node, children: mergeChildrenIntoParent(node.children, parentId, newNodes) }
        }
        return node
    })
}

/**
 * После PUT редактирования комментария: слить поля из CommentResource с узлом в кэше,
 * сохранив локальный массив children (ветки после «Показать ещё»).
 */
function parsePublishedTime(iso: string | null): number | null {
    if (iso == null || iso === "") return null
    const t = Date.parse(iso)
    return Number.isNaN(t) ? null : t
}

/**
 * Корень ленты комментариев: CommentIndexQuery + OrdersResults — по умолчанию
 * sort_by=published_at и sort_direction=desc (новее выше в списке).
 */
function insertRootCommentFeedDesc(roots: IGetCommentResponse[], newNode: IGetCommentResponse): IGetCommentResponse[] {
    const insertAt = roots.findIndex((r) => {
        const tNew = parsePublishedTime(newNode.published_at)
        const tR = parsePublishedTime(r.published_at)
        if (tNew !== null && tR !== null) {
            if (tNew !== tR) return tNew > tR
            return newNode.id > r.id
        }
        if (tNew !== null && tR === null) return true
        if (tNew === null && tR !== null) return false
        return newNode.id > r.id
    })
    const idx = insertAt === -1 ? roots.length : insertAt
    return [...roots.slice(0, idx), newNode, ...roots.slice(idx)]
}

/** Рекурсивно добавить ответ под parentId; вернуть { roots, found }. */
function addChildUnderParent(
    roots: IGetCommentResponse[],
    newNode: IGetCommentResponse,
    parentId: number
): { roots: IGetCommentResponse[]; found: boolean } {
    let found = false
    const next = roots.map((node) => {
        if (node.id === parentId) {
            found = true
            const existing = node.children ?? []
            const mergedChildren = sortCommentsByPublishedAtAsc([...existing, newNode])
            return {
                ...node,
                counters: {
                    ...node.counters,
                    children: node.counters.children + 1,
                },
                children: mergedChildren,
            }
        }
        if (!node.children?.length) return node
        const inner = addChildUnderParent(node.children, newNode, parentId)
        if (inner.found) {
            found = true
            return { ...node, children: inner.roots }
        }
        return node
    })
    return { roots: next, found }
}

/**
 * Вставить узел из POST создания комментария в кэш корневой ленты без полного рефетча.
 * Дочерние связи Comment::children() на бэке — orderBy created_at asc → после добавления сортируем как sortCommentsByPublishedAtAsc.
 * Если родитель не найден в дереве кэша — no-op (TODO: при необходимости точечный refetch).
 */
export function mergeNewCommentIntoCache(
    roots: IGetCommentResponse[],
    newNode: IGetCommentResponse,
    parentId: number | null | undefined,
    sort: CommentsSortUi = DEFAULT_COMMENTS_SORT,
): IGetCommentResponse[] {
    const normalized = { ...newNode, children: newNode.children }

    if (parentId == null || parentId === undefined) {
        if (sort === 'new') {
            return insertRootCommentFeedDesc(roots, normalized)
        }
        return [...roots, normalized]
    }

    const { roots: patched, found } = addChildUnderParent(roots, normalized, parentId)
    if (!found) {
        // Родитель не в кэше — без автоматического refetch (редкий случай).
        return roots
    }
    return patched
}


// После PUT редактирования комментария: слить поля из CommentResource с узлом в кэше,
// сохранив локальный массив children (ветки после «Показать ещё»).
export function patchCommentTextInTree(
    roots: IGetCommentResponse[],
    commentId: number,
    apiNode: IGetCommentResponse
): IGetCommentResponse[] {
    return patchCommentNodePreserveChildren(roots, commentId, apiNode)
}

export function removeCommentFromTree(
    roots: IGetCommentResponse[],
    commentId: number
): IGetCommentResponse[] {
    return roots
        .filter((node) => node.id !== commentId)
        .map((node) => ({
            ...node,
            children: node.children?.length
                ? removeCommentFromTree(node.children, commentId)
                : node.children,
        }))
}
